import { tryOnScopeDispose } from '@vueuse/core'
import Dexie, { liveQuery, type Table } from 'dexie'
import { ref } from 'vue'

export type UploadState =
  | 'idle' // Not ready to upload, no entity created yet
  | 'ready' // File stored in OPFS, nothing else done
  | 'creating-entity' // Creating entity for upload
  | 'getting-url' // Getting upload URL
  | 'uploading' // Upload in progress
  | 'confirming' // Confirming upload
  | 'done' // All done, can cleanup
  | 'error' // Upload failed

export type StateHistoryEntry = {
  state: UploadState
  timestamp: number
  error?: string
}

export type HubData = {
  workspaceId: string
  fileProjectId: string
  entityId?: string
}

export type FileProjectData = {
  id: string
  mainViewId?: string | null
  properties: Array<{
    id: string
    name: string
  }>
}

export type UploadRecord = {
  id?: string // Auto-generated

  // Field upload context
  workspaceId: string
  projectId: string
  propertyId: string
  parentEntityId?: string | null
  entityId?: string
  url?: string
  progress?: number

  originalPath: string // File path relative to the directory upload structure

  // File info
  opfsPath: string // Path in OPFS

  // Progress tracking
  status: UploadState

  // Timestamps
  createdAt: number
  updatedAt: number
  completedAt?: number
  error?: string
}

class UploadDatabase extends Dexie {
  uploads!: Table<UploadRecord>

  constructor() {
    super('HubUploads')
    this.version(1).stores({ uploads: '++id, status, createdAt' })
  }
}

let uploadDB = new UploadDatabase()
function getUploadDB() {
  if (!uploadDB) {
    uploadDB = new UploadDatabase()
  }
  return uploadDB
}

export function useFileUploadDB() {
  const db = getUploadDB()

  const isLoaded = ref(false)
  const allRecords = ref<UploadRecord[]>([])

  const dbObservable = liveQuery(() => db.uploads.orderBy('createdAt').toArray())
  const { unsubscribe } = dbObservable.subscribe(
    (uploads) => {
      allRecords.value = uploads
      isLoaded.value = true
    },
    (err) => {
      // eslint-disable-next-line no-console
      console.error('§ Error loading uploads', err)
    },
  )

  function find(id: string) {
    return db.uploads.get(id)
  }

  function put(record: UploadRecord): Promise<string> {
    return db.uploads.put(record)
  }

  function update(id: string, record: Partial<UploadRecord>) {
    return db.uploads.update(id, record)
  }

  function remove(id: string) {
    return db.uploads.delete(id)
  }

  tryOnScopeDispose(() => {
    unsubscribe()
  })

  return {
    isLoaded,
    allRecords,
    find,
    put,
    update,
    remove,
  }
}
